import { Tab } from "@headlessui/react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CustomRateForm } from "@/apps/partner/components/CustomRateForm/CustomRateForm";
import { MobileAboForm } from "@/apps/partner/components/MobileAboForm/MobileAboForm";
import { MobileAboSwitch } from "@/apps/partner/components/MobileAboForm/MobileAboSwitch";
import { NumberPorting } from "@/apps/partner/components/NumberPorting/NumberPorting";
import { NumberPortingChoice } from "@/apps/partner/components/NumberPorting/NumberPortingChoice";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { FreeEsimTrialBanner } from "@/components/Onboarding/Steps/RateSelection/FreeEsimTrialBanner/FreeEsimTrialBanner";
import {
  getMobileAbos,
  getSortedMobileAbosMonthly,
} from "@/components/Onboarding/Steps/RateSelection/rateSelection.utils";
import { Step } from "@/components/Onboarding/Steps/Step";
import { useIsOnboarding } from "@/hooks/useIsOnboarding";
import { useGetDevices, useGetOptions } from "@/services/api";
import {
  deviceNameToValueMap,
  getDeviceDescription,
} from "@/utils/deviceUtils";
import {
  isFlatMobileTariff,
  TransformedProduct,
  transformProducts,
} from "@/utils/tariffUtils";

export const RateSelection: FC = () => {
  // Hooks
  const { t } = useTranslation();
  const isOnboarding = useIsOnboarding();
  const {
    watch,
    setValue,
    setFocus,
    formState: { errors },
  } = useFormContext();

  // State
  const [numberPortingChoice, setNumberPortingChoice] = useState(false);
  const [shouldShowBestsellerTabs, setShouldShowBestsellerTabs] =
    useState(true);
  const [shouldShowNumberPorting, setShouldShowNumberPorting] = useState(true);

  // Refs
  const previousRateRef = useRef<string | undefined>(undefined);

  // Form values
  const currentRate = watch("productId");
  const selectedDeviceDescription = watch("device");
  const simType = watch("simType");

  // API calls
  const { data: devices } = useGetDevices({
    query: {
      cacheTime: 24 * 60 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  });

  const { data: availableOptions, isFetching: isLoadingOptions } =
    useGetOptions(131001, undefined, {
      query: {
        refetchOnWindowFocus: false,
        enabled: !!selectedDeviceDescription,
        onSuccess: (data) => transformProducts(data),
      },
    });

  // Derived values
  const selectedDevice = devices?.find(
    (device) => device.description === selectedDeviceDescription,
  );
  const device = selectedDevice?.description || "Smartphone";
  const isRouterOrHotspot =
    selectedDevice?.description?.toLowerCase().includes("router") ||
    selectedDevice?.description?.toLowerCase().includes("hotspot") ||
    false;
  const shouldShowCustomRatesTab = false;

  const options = useMemo(
    () => availableOptions && transformProducts(availableOptions),
    [availableOptions],
  );
  const { processedProductsById } = options || {};
  const selectedOption: TransformedProduct | undefined =
    processedProductsById?.[currentRate];

  const shouldShowEsimTrial = !!(isOnboarding && simType === "ESIM");
  const mobileAbos = getMobileAbos(processedProductsById);
  const prechargedProduct = availableOptions?.some(
    (product) => product.precharged,
  );
  const disableMobileAboSwitch = prechargedProduct;
  const isUsingTabbedSwitch =
    shouldShowBestsellerTabs &&
    !shouldShowCustomRatesTab &&
    !isRouterOrHotspot &&
    mobileAbos &&
    mobileAbos.length > 0;
  const isLoading = isLoadingOptions;

  // Functions
  const setRateFromMobileAbo = (rate: string) => {
    setValue("productId", Number(rate), { shouldValidate: false });
  };

  const handleRateTypeSwitch = ({
    shouldSwitchToBestsellerTab,
  }: {
    shouldSwitchToBestsellerTab: boolean;
  }) => {
    setShouldShowBestsellerTabs(shouldSwitchToBestsellerTab);
    document
      .getElementById("rate-section")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  // Update tariffName and tariffPrice when currentRate changes
  useEffect(() => {
    if (
      currentRate &&
      processedProductsById &&
      processedProductsById[currentRate]
    ) {
      const selectedProduct = processedProductsById[currentRate];

      setValue("tariffName", selectedProduct.baseDisplayValue || "", {
        shouldValidate: false,
      });
      setValue("tariffPrice", selectedProduct.chargeFlat || "", {
        shouldValidate: false,
      });
      setValue("tariffSpeed", selectedProduct.shortName || "", {
        shouldValidate: false,
      });
    }
  }, [currentRate, processedProductsById, setValue]);

  useEffect(() => {
    if (
      previousRateRef.current !== undefined &&
      previousRateRef.current !== currentRate
    ) {
      const targetElement = document.querySelector(`[name="numberporting"]`);

      if (targetElement) {
        // First scroll smoothly
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Wait for scroll animation to complete (typical smooth scroll takes ~500ms)
        // then set focus without additional scrolling
        setTimeout(() => {
          setFocus("onp", {
            shouldSelect: false,
          });
        }, 500);
      }
    }
    previousRateRef.current = currentRate;
  }, [currentRate, setFocus]);

  // Check which tab is selected on mount
  useEffect(() => {
    if (isUsingTabbedSwitch) {
      // Check if we're on a flat mobile tariff to determine initial tab
      const isFlatTariff = isFlatMobileTariff(
        selectedOption?.baseDisplayValue || "",
      );
      setShouldShowNumberPorting(isFlatTariff);
    }
  }, [isUsingTabbedSwitch, selectedOption]);

  return (
    <Step
      id="partner-rate-selection"
      headline={t("partnerPortal:addDevice.step3.title")}
      headlinePrimary={t(
        `Common.devices.${deviceNameToValueMap[getDeviceDescription(device)]}`,
      )}
    >
      <div id="rate-section">
        {shouldShowEsimTrial && <FreeEsimTrialBanner />}
        {isLoading && <LoadingSpinner />}
        {isUsingTabbedSwitch ? (
          <MobileAboSwitch
            initialIndex={
              selectedOption?.baseDisplayValue
                ? isFlatMobileTariff(selectedOption?.baseDisplayValue || "")
                  ? 0
                  : 1 || 0
                : 0
            }
            disabled={disableMobileAboSwitch}
            onTabChange={(idx) => {
              setShouldShowNumberPorting(idx === 0);
              setNumberPortingChoice(false);
            }}
          >
            {errors.productId && !currentRate && (
              <span className="mt-2 flex justify-center text-sm text-error-100">
                {String(errors.productId.message)}
              </span>
            )}
            <Tab.Panels>
              <Tab.Panel>
                <MobileAboForm
                  value={currentRate ? String(currentRate) : undefined}
                  setValue={setRateFromMobileAbo}
                  mobileAbos={getSortedMobileAbosMonthly(mobileAbos)}
                  showCustomRateTab={() =>
                    handleRateTypeSwitch({
                      shouldSwitchToBestsellerTab: false,
                    })
                  }
                  isCustomRateTabLinkDisabled={disableMobileAboSwitch}
                />
              </Tab.Panel>
              <Tab.Panel>
                <CustomRateForm
                  selectedOption={selectedOption}
                  options={options}
                  currentRate={currentRate ? String(currentRate) : ""}
                  shouldShowEsimTrial={shouldShowEsimTrial}
                  showBestsellerTabs={() =>
                    handleRateTypeSwitch({
                      shouldSwitchToBestsellerTab: true,
                    })
                  }
                  isBestsellerTabsLinkDisabled={prechargedProduct}
                />
              </Tab.Panel>
            </Tab.Panels>
          </MobileAboSwitch>
        ) : (
          <CustomRateForm
            selectedOption={selectedOption}
            options={options}
            currentRate={currentRate ? String(currentRate) : ""}
            shouldShowEsimTrial={shouldShowEsimTrial}
            showBestsellerTabs={() =>
              handleRateTypeSwitch({ shouldSwitchToBestsellerTab: true })
            }
            isBestsellerTabsLinkDisabled={prechargedProduct}
          />
        )}
        {shouldShowNumberPorting && (
          <NumberPortingChoice
            optionSelected={!!selectedOption}
            setNumberPortingChoice={setNumberPortingChoice}
          />
        )}
      </div>
      {numberPortingChoice && <NumberPorting />}
    </Step>
  );
};
